/**
 * 分档累进 / 插值计费工具（本地测算，逻辑对齐三份 Excel 计算表）
 */

/** @param {number} n @param {number} digits */
export function round(n, digits = 6) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  const p = 10 ** digits;
  return Math.round(Number(n) * p) / p;
}

/**
 * 分档累进：brackets = [{ end, rate }]，end 为区间上限（含），末档 end 可为 Infinity
 * 返回 { fee, rows }，rows 为各档明细
 */
export function progressive(amount, brackets) {
  const base = Math.max(0, Number(amount) || 0);
  const rows = [];
  let fee = 0;
  let prev = 0;

  for (let i = 0; i < brackets.length; i++) {
    const {end, rate, label} = brackets[i];
    const upper = end == null || end === Infinity ? Infinity : Number(end);
    const start = prev;
    const segment = Math.max(0, Math.min(base, upper) - start);
    const part = segment * rate;
    rows.push({
      index: i + 1,
      label: label || `${start}-${upper === Infinity ? '以上' : upper}`,
      start,
      end: upper === Infinity ? null : upper,
      rate,
      segment,
      fee: part,
    });
    fee += part;
    if (base <= upper) break;
    prev = upper;
  }

  return {fee, rows, amount: base};
}

/**
 * 监理费固定收费点插值
 * points: [{base, fee}, ...] 按 base 升序
 * amount ≤ 首点时按比例折算；超出末点时按末两段斜率外推
 */
export function interpolateFee(amount, points) {
  const a = Math.max(0, Number(amount) || 0);
  const pts = (points || []).slice().sort((x, y) => x.base - y.base);
  if (!pts.length || a <= 0) return 0;
  if (a <= pts[0].base) {
    return pts[0].base ? (pts[0].fee * a) / pts[0].base : 0;
  }
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    if (a <= p1.base) {
      return p0.fee + ((p1.fee - p0.fee) * (a - p0.base)) / (p1.base - p0.base);
    }
  }
  const p0 = pts[pts.length - 2];
  const p1 = pts[pts.length - 1];
  return p0.fee + ((p1.fee - p0.fee) * (a - p0.base)) / (p1.base - p0.base);
}

/** 金额大写（元） */
export function amountInWords(n) {
  const fraction = ['角', '分'];
  const digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
  const unit = [
    ['元', '万', '亿'],
    ['', '拾', '佰', '仟'],
  ];
  const head = n < 0 ? '负' : '';
  let num = Math.abs(Number(n) || 0);
  let s = '';

  // 角分
  for (let i = 0; i < fraction.length; i++) {
    s += (digit[Math.floor(num * 10 * 10 ** i) % 10] + fraction[i]).replace(/零./, '');
  }
  s = s || '整';
  num = Math.floor(num);

  for (let i = 0; i < unit[0].length && num > 0; i++) {
    let p = '';
    for (let j = 0; j < unit[1].length && num > 0; j++) {
      p = digit[num % 10] + unit[1][j] + p;
      num = Math.floor(num / 10);
    }
    s = p.replace(/(零.)*零$/, '').replace(/^$/, '零') + unit[0][i] + s;
  }

  return (
    head +
    s
      .replace(/(零.)*零元/, '元')
      .replace(/(零.)+/g, '零')
      .replace(/^整$/, '零元整')
  );
}

// --------------- 招标代理费 《计价格[2002]1980号》 ---------------

export const AGENCY_TYPES = {
  engineering: {
    key: 'engineering',
    name: '工程',
    title: '工程招标代理收费计算表',
    brackets: [
      {end: 100, rate: 0.01, label: '0-100'},
      {end: 500, rate: 0.007, label: '100-500'},
      {end: 1000, rate: 0.0055, label: '500-1000'},
      {end: 5000, rate: 0.0035, label: '1000-5000'},
      {end: 10000, rate: 0.002, label: '5000-10000'},
      {end: 100000, rate: 0.0005, label: '10000-100000'},
      {end: Infinity, rate: 0.0001, label: '100000以上'},
    ],
  },
  goods: {
    key: 'goods',
    name: '货物',
    title: '货物招标代理收费计算表',
    brackets: [
      {end: 100, rate: 0.015, label: '0-100'},
      {end: 500, rate: 0.011, label: '100-500'},
      {end: 1000, rate: 0.008, label: '500-1000'},
      {end: 5000, rate: 0.005, label: '1000-5000'},
      {end: 10000, rate: 0.0025, label: '5000-10000'},
      {end: 100000, rate: 0.0005, label: '10000-100000'},
      {end: Infinity, rate: 0.0001, label: '100000以上'},
    ],
  },
  service: {
    key: 'service',
    name: '服务',
    title: '服务招标代理收费计算表',
    brackets: [
      {end: 100, rate: 0.015, label: '0-100'},
      {end: 500, rate: 0.008, label: '100-500'},
      {end: 1000, rate: 0.0045, label: '500-1000'},
      {end: 5000, rate: 0.0025, label: '1000-5000'},
      {end: 10000, rate: 0.001, label: '5000-10000'},
      {end: 100000, rate: 0.0005, label: '10000-100000'},
      {end: Infinity, rate: 0.0001, label: '100000以上'},
    ],
  },
};

/**
 * @param {number} costWan 计算造价（万元）
 * @param {'engineering'|'goods'|'service'} type
 * @param {number} discountRate 让利率，如 -0.3 表示下浮 30%；也可传 0.3 表示下浮 30%
 */
export function calcAgencyFee(costWan, type = 'engineering', discountRate = 0) {
  const conf = AGENCY_TYPES[type] || AGENCY_TYPES.engineering;
  const {fee: subtotal, rows} = progressive(costWan, conf.brackets);
  let rate = Number(discountRate) || 0;
  // Excel 中让利填 -0.3；若用户填正数则按下浮理解
  if (rate > 0) rate = -rate;
  const concession = subtotal * rate;
  const total = subtotal + concession;
  return {
    type: conf.key,
    title: conf.title,
    costWan: Number(costWan) || 0,
    rows,
    subtotal: round(subtotal, 6),
    discountRate: rate,
    concession: round(concession, 6),
    totalWan: round(total, 6),
    totalYuan: round(total * 10000, 2),
    words: amountInWords(round(total * 10000, 2)),
  };
}

// --------------- 浙江造价咨询收费 浙价服〔2009〕84号 ---------------

/** 分档：万元；费率单位为 ‰；收费(元)=造价(万元)×费率‰×10 */
export const COST_BRACKETS_LABELS = [
  '100万元以内',
  '101-500万元',
  '501-1000万元',
  '1001-2000万元',
  '2001-5000万元',
  '5001-10000万元',
  '10000万元以上',
];

const COST_ENDS = [100, 500, 1000, 2000, 5000, 10000, Infinity];

function makeCostBrackets(rates) {
  return rates.map((rate, i) => ({
    end: COST_ENDS[i],
    rate,
    label: COST_BRACKETS_LABELS[i],
  }));
}

export const COST_CONSULTING_ITEMS = [
  {
    key: 'estimate',
    no: 1,
    name: '投资估算编制或审核',
    baseLabel: '估算价',
    rates: [1.3, 1.1, 0.9, 0.7, 0.6, 0.5, 0.4],
  },
  {
    key: 'design_budget',
    no: 2,
    name: '设计概算编制或审核',
    baseLabel: '概算价',
    rates: [1.7, 1.5, 1.3, 1.1, 1.0, 0.9, 0.8],
  },
  {
    key: 'bq_alone',
    no: 3,
    name: '工程量清单单独编制或审核',
    group: '清单计价法',
    baseLabel: '建安工程造价',
    rates: [2.9, 2.7, 2.5, 2.3, 2.1, 1.9, 1.7],
  },
  {
    key: 'bid_quote',
    no: 4,
    name: '投标报价编制',
    group: '清单计价法',
    baseLabel: '建安工程造价',
    rates: [1.4, 1.2, 1.0, 0.8, 0.6, 0.5, 0.4],
  },
  {
    key: 'bq_control',
    no: 5,
    name: '工程量清单及预算、招标控制价的编制或审核',
    group: '清单计价法',
    baseLabel: '建安工程造价',
    rates: [3.6, 3.3, 3.0, 2.7, 2.4, 2.1, 1.8],
  },
  {
    key: 'quota_price',
    no: 6,
    name: '预算、招标控制价、投标报价的编制或审核',
    group: '定额计价法',
    baseLabel: '建安工程造价',
    rates: [3.0, 2.5, 2.3, 2.1, 1.9, 1.7, 1.5],
  },
  {
    key: 'settlement_make',
    no: 7,
    name: '工程结算编制',
    baseLabel: '建安工程造价',
    rates: [2.5, 2.2, 1.9, 1.6, 1.3, 1.0, 0.7],
  },
  {
    key: 'settlement_audit',
    no: 8,
    name: '工程结算审核（基本收费）',
    baseLabel: '送审工程造价',
    rates: [2.8, 2.5, 2.2, 1.9, 1.6, 1.3, 1.0],
    extra: {
      key: 'settlement_extra',
      name: '追加费用（超过5%以外的核减额、核增额）',
      /** 核增减额(万元) → 元：额×10000×5% */
      rateYuan: 0.05,
    },
  },
  {
    key: 'final_account',
    no: 9,
    name: '竣工决算编制或审核',
    baseLabel: '建设项目总费用',
    rates: [2.0, 1.6, 1.2, 0.8, 0.5, 0.3, 0.1],
  },
  {
    key: 'whole_process',
    no: 10,
    name: '施工阶段全过程工程造价控制',
    baseLabel: '建安工程造价',
    rates: [12.0, 11.0, 10.0, 9.0, 7.0, 6.0, 5.0],
  },
];

const MIN_COST_FEE = 2000;

/**
 * @param {number} costWan 造价（万元）
 * @param {number[]} rates ‰
 * @param {number} discount 折扣率，如 0.6
 * @param {{float20?: boolean}} opts 装饰/安装等上浮20%
 */
export function calcCostConsultingFee(costWan, rates, discount = 1, opts = {}) {
  const brackets = makeCostBrackets(rates);
  // progressive 用的是「万元 × ‰」得到的中间值，再 ×10 得元
  const {fee: mid, rows} = progressive(costWan, brackets);
  let standard = mid * 10;
  if (opts.float20) standard *= 1.2;
  const disc = discount == null || discount === '' ? 1 : Number(discount);
  let charged = standard * disc;
  const belowMin = charged > 0 && charged < MIN_COST_FEE;
  if (belowMin) charged = MIN_COST_FEE;
  return {
    costWan: Number(costWan) || 0,
    rows: rows.map((r) => ({...r, feeYuan: round(r.fee * 10, 2)})),
    standardYuan: round(standard, 2),
    discount: disc,
    chargedYuan: round(charged, 2),
    belowMin,
  };
}

/** 结算审核追加：核增减额（万元）× 5% × 10000 */
export function calcSettlementExtra(extraWan, discount = 1) {
  const wan = Math.max(0, Number(extraWan) || 0);
  const standard = wan * 10000 * 0.05;
  const disc = discount == null || discount === '' ? 1 : Number(discount);
  return {
    extraWan: wan,
    standardYuan: round(standard, 2),
    chargedYuan: round(standard * disc, 2),
  };
}

// --------------- 项目管理及监理费用（2018） ---------------

export const PM_FEE_DEFS = [
  {
    key: 'cm_2018',
    name: '建设管理费（2018新）',
    baseKey: 'investMinusLand',
    baseLabel: '计费基数=总投资－土地费用',
    unit: 'wan',
    brackets: [
      {end: 1000, rate: 0.023, label: '0-1000'},
      {end: 5000, rate: 0.017, label: '1000-5000'},
      {end: 10000, rate: 0.014, label: '5000-10000'},
      {end: 50000, rate: 0.012, label: '10000-50000'},
      {end: 100000, rate: 0.009, label: '50000-100000'},
      {end: Infinity, rate: 0.005, label: '100000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'agency_new',
    name: '招标代理费（新）',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.01, label: '0-100'},
      {end: 500, rate: 0.007, label: '100-500'},
      {end: 1000, rate: 0.0055, label: '500-1000'},
      {end: 5000, rate: 0.0035, label: '1000-5000'},
      {end: 10000, rate: 0.002, label: '5000-10000'},
      {end: 50000, rate: 0.0005, label: '10000-50000'},
      {end: 100000, rate: 0.00035, label: '50000-100000'},
      {end: 500000, rate: 0.00008, label: '100000-500000'},
      {end: 1000000, rate: 0.00006, label: '500000-1000000'},
      {end: Infinity, rate: 0.00004, label: '1000000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'cost_control',
    name: '全过程造价控制费',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.012, label: '0-100'},
      {end: 500, rate: 0.011, label: '100-500'},
      {end: 1000, rate: 0.01, label: '500-1000'},
      {end: 2000, rate: 0.009, label: '1000-2000'},
      {end: 5000, rate: 0.007, label: '2000-5000'},
      {end: 10000, rate: 0.006, label: '5000-10000'},
      {end: Infinity, rate: 0.005, label: '10000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'control_price_only',
    name: '仅招标控制价编制',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.0036, label: '0-100'},
      {end: 500, rate: 0.0033, label: '100-500'},
      {end: 1000, rate: 0.003, label: '500-1000'},
      {end: 2000, rate: 0.0027, label: '1000-2000'},
      {end: 5000, rate: 0.0024, label: '2000-5000'},
      {end: 10000, rate: 0.0021, label: '5000-10000'},
      {end: Infinity, rate: 0.0018, label: '10000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'settlement_audit',
    name: '工程结算审核费',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.0028, label: '0-100'},
      {end: 500, rate: 0.0025, label: '100-500'},
      {end: 1000, rate: 0.0022, label: '500-1000'},
      {end: 2000, rate: 0.0019, label: '1000-2000'},
      {end: 5000, rate: 0.0016, label: '2000-5000'},
      {end: 10000, rate: 0.0013, label: '5000-10000'},
      {end: Infinity, rate: 0.001, label: '10000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'invest_estimate_audit',
    name: '投资估算审核费',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.0013, label: '0-100'},
      {end: 500, rate: 0.0011, label: '100-500'},
      {end: 1000, rate: 0.0009, label: '500-1000'},
      {end: 2000, rate: 0.0007, label: '1000-2000'},
      {end: 5000, rate: 0.0006, label: '2000-5000'},
      {end: 10000, rate: 0.0005, label: '5000-10000'},
      {end: Infinity, rate: 0.0004, label: '10000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'final_finance',
    name: '竣工财务决算编制费',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.002, label: '0-100'},
      {end: 500, rate: 0.0016, label: '100-500'},
      {end: 1000, rate: 0.0012, label: '500-1000'},
      {end: 2000, rate: 0.0008, label: '1000-2000'},
      {end: 5000, rate: 0.0005, label: '2000-5000'},
      {end: 10000, rate: 0.0003, label: '5000-10000'},
      {end: Infinity, rate: 0.0001, label: '10000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'invest_budget_audit',
    name: '投资概算审核费',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'wan',
    brackets: [
      {end: 100, rate: 0.0017, label: '0-100'},
      {end: 500, rate: 0.0015, label: '100-500'},
      {end: 1000, rate: 0.0013, label: '500-1000'},
      {end: 2000, rate: 0.0011, label: '1000-2000'},
      {end: 5000, rate: 0.001, label: '2000-5000'},
      {end: 10000, rate: 0.0009, label: '5000-10000'},
      {end: Infinity, rate: 0.0008, label: '10000以上'},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'supervision_2018',
    name: '施工监理费(2018)',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'interp',
    applyFactors: true,
    points: [
      {base: 500, fee: 13.2},
      {base: 1000, fee: 24.1},
      {base: 3000, fee: 62.5},
      {base: 5000, fee: 96.6},
      {base: 8000, fee: 144.8},
      {base: 10000, fee: 174.9},
      {base: 20000, fee: 314.7},
      {base: 40000, fee: 566.6},
      {base: 60000, fee: 793.1},
      {base: 80000, fee: 1004.6},
      {base: 100000, fee: 1205.6},
      {base: 200000, fee: 2170},
    ],
    defaultDiscount: 0,
  },
  {
    key: 'supervision_old',
    name: '施工监理费（旧表）',
    baseKey: 'construction',
    baseLabel: '计费基数=建安造价',
    unit: 'interp',
    applyFactors: true,
    points: [
      {base: 500, fee: 16.5},
      {base: 1000, fee: 30.1},
      {base: 3000, fee: 78.1},
      {base: 5000, fee: 120.8},
      {base: 8000, fee: 181},
      {base: 10000, fee: 218.6},
      {base: 20000, fee: 393.4},
      {base: 40000, fee: 708.2},
      {base: 60000, fee: 991.4},
      {base: 80000, fee: 1255.8},
      {base: 100000, fee: 1507},
      {base: 200000, fee: 2712.5},
    ],
    defaultDiscount: 0,
  },
];

/**
 * @param {object} project
 * @param {object} options enabled/discount map
 */
export function calcPmSupervision(project, options = {}) {
  const totalInvest = Number(project.totalInvest) || 0;
  const landCost = Number(project.landCost) || 0;
  const construction = Number(project.construction) || 0;
  const area = Number(project.area) || 0;
  const specialty = Number(project.specialtyFactor) || 1;
  const complexity = Number(project.complexityFactor) || 1;
  const elevation = Number(project.elevationFactor) || 1;
  const factor = specialty * complexity * elevation;

  const bases = {
    construction,
    investMinusLand: Math.max(0, totalInvest - landCost),
  };

  const items = PM_FEE_DEFS.map((def) => {
    const opt = options[def.key] || {};
    const enabled = opt.enabled !== false;
    const discount = opt.discount != null ? Number(opt.discount) : def.defaultDiscount || 0;
    const base = bases[def.baseKey] || 0;
    let standard = 0;
    let detailRows = [];

    if (!enabled || base <= 0) {
      return {
        key: def.key,
        name: def.name,
        baseLabel: def.baseLabel,
        base,
        enabled,
        discount,
        standard: 0,
        afterDiscount: 0,
        unitPrice: 0,
        rows: [],
      };
    }

    if (def.unit === 'interp') {
      standard = interpolateFee(base, def.points);
      if (def.applyFactors) standard *= factor;
    } else {
      const r = progressive(base, def.brackets);
      standard = r.fee;
      detailRows = r.rows;
    }

    const afterDiscount = standard * (1 - discount);
    const unitPrice = area > 0 ? (afterDiscount * 10000) / area : 0;

    return {
      key: def.key,
      name: def.name,
      baseLabel: def.baseLabel,
      base,
      enabled,
      discount,
      standard: round(standard, 6),
      afterDiscount: round(afterDiscount, 6),
      unitPrice: round(unitPrice, 4),
      rows: detailRows,
    };
  });

  const enabledItems = items.filter((i) => i.enabled);
  const sumStandard = enabledItems.reduce((s, i) => s + i.standard, 0);
  const sumAfter = enabledItems.reduce((s, i) => s + i.afterDiscount, 0);
  const sumUnit = area > 0 ? (sumAfter * 10000) / area : 0;

  return {
    project: {
      totalInvest,
      landCost,
      construction,
      area,
      specialty,
      complexity,
      elevation,
      factor: round(factor, 6),
      investMinusLand: bases.investMinusLand,
    },
    items,
    sumStandard: round(sumStandard, 6),
    sumAfter: round(sumAfter, 6),
    sumUnit: round(sumUnit, 4),
  };
}
